import { useCallback } from 'react';

export interface StoredPhoto {
  name: string;
  type: string;
  data: string;
  processed?: boolean;
  processedAt?: string;
}

const DB_NAME = 'emberDraftPhotos';
const STORE_NAME = 'photos';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
  });
}

async function syncPhotoToIndexedDB(formKey: string, photo: StoredPhoto | null): Promise<void> {
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      if (photo) {
        store.put(photo, formKey);
      } else {
        store.delete(formKey);
      }
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to sync photo to IndexedDB');
        db.close();
        reject(err);
      };
      tx.onabort = () => {
        const err = tx.error ?? new Error('Photo transaction aborted');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('IndexedDB photo sync failed', error);
  }
}

async function loadPhotoFromIndexedDB(formKey: string): Promise<StoredPhoto | null> {
  try {
    const db = await openDatabase();
    return await new Promise<StoredPhoto | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(formKey);
      request.onsuccess = () => {
        db.close();
        resolve((request.result as StoredPhoto | undefined) ?? null);
      };
      request.onerror = () => {
        const err = request.error ?? new Error('Failed to load photo from IndexedDB');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('IndexedDB photo load failed', error);
    return null;
  }
}

export function usePhotoStorage(formKey: string) {
  const storageKey = `${formKey}_photo`;

  const loadPhotoSync = useCallback((): StoredPhoto | null => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && typeof parsed.data === 'string') {
        return parsed as StoredPhoto;
      }
    } catch (error) {
      console.warn('Failed to load photo from sessionStorage', error);
    }
    return null;
  }, [storageKey]);

  const loadPhoto = useCallback(
    async (): Promise<StoredPhoto | null> => {
      const sessionPhoto = loadPhotoSync();
      if (sessionPhoto) {
        return sessionPhoto;
      }
      const indexed = await loadPhotoFromIndexedDB(formKey);
      if (indexed) {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(indexed));
        } catch {
          // ignore
        }
      }
      return indexed;
    },
    [formKey, storageKey]
  );

  const loadPhotoFromBackup = useCallback(async (): Promise<StoredPhoto | null> => {
    return loadPhoto();
  }, [loadPhoto]);

  const savePhotoData = useCallback(
    async (photo: StoredPhoto): Promise<StoredPhoto> => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(photo));
      } catch (error) {
        console.warn('Failed to save photo to sessionStorage', error);
      }
      setTimeout(() => {
        void syncPhotoToIndexedDB(formKey, photo);
      }, 0);
      return photo;
    },
    [formKey, storageKey]
  );

  const savePhoto = useCallback(
    (file: File): Promise<StoredPhoto | null> =>
      new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onload = async (event) => {
            const dataUrl = event.target?.result;
            if (typeof dataUrl === 'string') {
              const stored: StoredPhoto = {
                name: file.name,
                type: file.type || 'image/jpeg',
                data: dataUrl,
              };
              try {
                await savePhotoData(stored);
                resolve(stored);
              } catch (error) {
                reject(error);
              }
            } else {
              resolve(null);
            }
          };
          reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo'));
          reader.readAsDataURL(file);
        } catch (error) {
          reject(error);
        }
      }),
    [savePhotoData]
  );

  const clearPhoto = useCallback(() => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear photo from sessionStorage', error);
    }
    void syncPhotoToIndexedDB(formKey, null);
  }, [formKey, storageKey]);

  return {
    loadPhoto,
    loadPhotoSync,
    loadPhotoFromBackup,
    savePhoto,
    savePhotoData,
    clearPhoto,
  };
}

